import React, { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Link, useNavigate } from "react-router-dom";
import { doc, onSnapshot, updateDoc, deleteField } from "firebase/firestore";
import { db } from "../../firebase";
import { importKey, decryptPrivateKey } from "../../utils/crypto";
import Loader from "../Loader";
import { signInWithCustomToken } from "firebase/auth";
import { auth } from "../../firebase";



export default function LinkDevice() {
    const [linkData, setLinkData] = useState(null);
    const [status, setStatus] = useState("Generating Secure Link...");
    const navigate = useNavigate();

    useEffect(() => {
        // 1. Generate Link Session ID & Temporary Transport Key
        const sessionId = crypto.randomUUID();
        const transportKey = crypto.randomUUID().replace(/-/g, ""); // Simple high-entropy string for AES key derivation on Android side if needed, or just use as raw key material.

        // Actually, for better security, let's generate a proper AES-GCM Key and export it?
        // To keep it simple for QR:
        // We will send { id: sessionId, key: transportKey }
        // Android will use 'transportKey' to encrypt the payload.

        setLinkData({
            id: sessionId,
            key: transportKey
        });
        setStatus("Scan this QR code with your Mobile App");

        // 2. Listen for response
        const linkRef = doc(db, "temporary_links", sessionId);

        const unsub = onSnapshot(linkRef, async (snap) => {
            if (snap.exists()) {
                const data = snap.data();
                if (data.encryptedPayload) {
                    setStatus("Synchronizing keys...");

                    try {
                        

                        // 3. Decrypt Payload
                        // Android encrypted { uid, email, password, privKey, pubKey }
                        // Using PBKDF2 derived key from 'transportKey'
                        // decryptPrivateKey expects { encrypted, salt, iv } and the password (transportKey)
                        // It returns a string, which should be the JSON string of the payload

                        const decryptedString = await decryptPrivateKey(data.encryptedPayload, transportKey);
                        const { uid, privKey, pubKey, email, password } = JSON.parse(decryptedString);

                        // 4. Save to LocalStorage
                        localStorage.setItem(`privKey_${uid}`, privKey);
                        localStorage.setItem(`pubKey_${uid}`, pubKey);

                        // 💎 CRITICAL FIX: Generate Device ID and Set Login Flag
                        // This prevents App.js from seeing a "Session Mismatch" and logging us out immediately.
                        let deviceId = localStorage.getItem("deviceId");
                        if (!deviceId) {
                            deviceId = crypto.randomUUID();
                            localStorage.setItem("deviceId", deviceId);
                        }
                        sessionStorage.setItem("isLoggingIn", "true");

                        // 5. Login with Credentials
                        if (email && password) {
                            await signInWithEmailAndPassword(auth, email, password);
                            setStatus("Login Successful!");
                        } else {
                            // Fallback
                            setStatus("Keys Synced. Please Login manually.");
                            navigate("/login");
                            return;
                        }

                        // Cleanup
                        await updateDoc(linkRef, { consumed: true });

                        navigate("/");

                    } catch (e) {
                        console.error("Link failed:", e);
                        setStatus("Linking failed. Please try again.");
                    }
                }
            }
        }, (err) => {
            console.error("🔥 Firestore Error:", err);
            setStatus("Connection Error: " + err.message);
        });

        return () => unsub();
    }, [navigate]);

    if (!linkData) return <Loader text="Preparing Secure Link..." />;

    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 p-4">
            <div className="bg-white p-8 rounded-2xl shadow-xl max-w-sm w-full text-center">
                <h2 className="text-2xl font-bold mb-2">Link Device</h2>
                <p className="text-gray-500 mb-6 text-sm">
                    Open ChatIQ on your mobile,<br />go to Settings {'>'} Link Device
                </p>

                <div className="bg-white p-4 border-4 border-slate-100 rounded-xl inline-block mb-6 relative">
                    <QRCodeCanvas
                        value={JSON.stringify(linkData)}
                        size={220}
                        level={"H"}
                        includeMargin={true}
                    />
                    {/* Logo Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center p-1 shadow-sm">
                            <div className="w-full h-full bg-blue-600 rounded-full"></div>
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="flex items-center justify-center space-x-2 text-sm text-slate-600">
                        {status === "Synchronizing keys..." ? (
                            <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                        ) : (
                            <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                        )}
                        <span className="font-medium animate-pulse">{status}</span>
                    </div>

                    <Link to="/login" className="block text-blue-600 font-semibold text-sm hover:underline">
                        Cancel
                    </Link>
                </div>
            </div>
        </div>
    );
}

// Helper to handle the "Login with QR" flow needing imports
import { signInWithEmailAndPassword } from "firebase/auth";
